/**
 * Stripe cost check campaign form — /stripe-cost-check. Enquiries are routed
 * to Sam and must NOT be enrolled into marketing (campaign brief 7 Sep 2026),
 * so there is no marketing opt-in and consent.marketing is forced false.
 */
import { Mail, Phone } from "lucide-react";
import analytics from "@/utils/analytics";
import { companyRequired, email, firstName, lastName, phone, turnoverBand, website } from "./common";

export const SAM_EMAIL = "sam@candopayments.com";
const SAM_MAILTO = `mailto:${SAM_EMAIL}`;

const stripeCostCheck = {
  id: "stripe_cost_check",
  label: "Stripe cost check",
  crm: {
    layout: "Standard",
    leadSource: "Website Campaign",
    tags: ["Campaign: Stripe cost check", "Route: Sam"],
  },
  fields: [
    [firstName, lastName],
    companyRequired,
    [
      { ...email, label: "Work email" },
      { ...phone, label: "Phone number (optional)", autoComplete: "tel" },
    ],
    { ...website, label: "Website (optional)" },
    { ...turnoverBand, name: "online_turnover_band", label: "Monthly online turnover (optional)" },
  ],
  submitLabel: "Request your free cost review",
  consent: { variant: "notice", marketing: false },
  footer: (
    <div className="space-y-3">
      {/* Microcopy verbatim from the 9 Sep 2026 pack. */}
      <p className="text-xs text-brand-grey leading-relaxed" data-testid="scc-form-documents-note">
        You don&rsquo;t need to send any documents yet. Sam will confirm what&rsquo;s useful and how to share it securely.
      </p>
      {/* Direct contact routes to Sam for this campaign, not the shared hello@ inbox. */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-x-5 gap-y-1.5 text-sm text-brand-grey pt-2">
        <span>Prefer to email? Pop Sam an email, or fill in the short form above.</span>
        <a
          href={SAM_MAILTO}
          className="inline-flex items-center gap-1.5 font-semibold text-brand-aqua-dark hover:text-brand-aqua transition-colors"
          data-testid="scc-form-email-link"
        >
          <Mail size={14} />{SAM_EMAIL}
        </a>
        <a
          href="tel:+448003682989"
          onClick={() => analytics.clickPhone()}
          className="inline-flex items-center gap-1.5 font-semibold text-brand-aqua-dark hover:text-brand-aqua transition-colors"
          data-testid="scc-form-phone-link"
          data-measurement-owned="contact"
        >
          <Phone size={14} /> 0800 368 2989
        </a>
      </div>
    </div>
  ),
  // Rendered only client-side after a failed request, so markup is safe here.
  errorMessage: (
    <>
      {"Your request hasn’t gone through. Please try again, or email Sam at "}
      <a href={SAM_MAILTO} className="font-semibold underline underline-offset-2">
        {SAM_EMAIL}
      </a>
      .
    </>
  ),
  // Confirmation copy verbatim from the campaign brief.
  success: {
    title: "Thanks — we’ve received your request.",
    body: "Sam will get in touch to discuss your Stripe cost review.",
  },
  contextToCrm: () => ({
    Current_Supplier: "Stripe",
    Needs: ["Online payments"],
    How_do_you_take_payments_now: ["Online payments"],
  }),
  summary: () => "Stripe cost review enquiry",
  // Landing-page funnel events kept from the previous form.
  events: {
    start: () => analytics.landingEvent("scc_form_start"),
    submit: () => analytics.landingEvent("scc_form_submit"),
    success: () => analytics.landingEvent("scc_form_success"),
    invalid: () => analytics.landingEvent("scc_form_error", { reason: "validation" }),
    error: () => analytics.landingEvent("scc_form_error", { reason: "request" }),
  },
};

export default stripeCostCheck;
